import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, onSnapshot, query, doc, updateDoc, 
  setDoc, Timestamp, where, deleteDoc, getDoc
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import moment from 'moment';

function Purchasestock() {
  const [fuelType, setFuelType] = useState("MS"); // Toggle state: MS or HSD
  const [orderdate, setOrderdate] = useState(moment().format("YYYY-MM-DD"));
  const [tanks, setTanks] = useState([]);
  const [purchaseData, setPurchaseData] = useState({});
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const userId = auth.currentUser?.uid;

  const Toast = Swal.mixin({
    toast: true, position: 'top-end', showConfirmButton: false, timer: 3000,
  });

  // 1. Fetch Tanks filtered by current fuelType (MS or HSD)
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, "tanks"), 
      where("userId", "==", userId), 
      where('fuelType', "==", fuelType)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const tankList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTanks(tankList);
      
      const initialForm = {};
      tankList.forEach(t => {
        initialForm[`${t.id}_current`] = t.currentStock || 0;
        initialForm[`${t.id}_rate`] = t.purchaseRate || 0;
        initialForm[`${t.id}_quantity`] = "";
        initialForm[`${t.id}_invoice`] = "";
      });
      setPurchaseData(initialForm);
    });
    return () => unsubscribe();
  }, [userId, fuelType]);

  // 2. Fetch Purchase History filtered by fuelType
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, "purchase_ledger"), 
      where("userId", "==", userId),
      where("fuelType", "==", fuelType)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHistory(data.sort((a, b) => b.date.localeCompare(a.date)));
    });
    return () => unsubscribe();
  }, [userId, fuelType]);

  const handleInputChange = (field, value) => {
    setPurchaseData(prev => ({ ...prev, [field]: value }));
  };

  // 3. Delete Purchase and Rollback Stock (Minus Stock)
  const deleteEntry = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this purchase record?',
      text: "Tank stock will be reverted (decreased)!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        const docRef = doc(db, "purchase_ledger", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const purchaseRecord = docSnap.data();
          
          // Revert the tank stocks by subtracting the items purchased
          for (const item of purchaseRecord.tankPurchases) {
            const tankRef = doc(db, "tanks", item.tankId);
            const tankSnap = await getDoc(tankRef);
            if (tankSnap.exists()) {
              const currentTankStock = parseFloat(tankSnap.data().currentStock || 0);
              await updateDoc(tankRef, {
                currentStock: Math.max(0, currentTankStock - item.quantity)
              });
            }
          }
        }

        await deleteDoc(docRef);
        Toast.fire({ icon: 'success', title: 'Purchase Entry Deleted & Stock Reverted' });
      } catch (err) {
        Toast.fire({ icon: 'error', title: err.message });
      }
    }
  };

  // 4. Save Purchase and Update Tank Stock (Plus Stock)
  const savePurchaseStock = async (e) => {
    e.preventDefault();
    if (!userId) return;
    setLoading(true);
    try {
      let totalLiters = 0;
      let totalAmount = 0;
      
      const tankPurchases = tanks.map(t => {
        const currentStock = parseFloat(purchaseData[`${t.id}_current`] || 0);
        const quantity = parseFloat(purchaseData[`${t.id}_quantity`] || 0);
        const rt = parseFloat(purchaseData[`${t.id}_rate`] || 0);
        const invoice = purchaseData[`${t.id}_invoice`] || '';
        
        const amount = quantity * rt;
        
        if (quantity > 0) { 
          totalLiters += quantity; 
          totalAmount += amount; 
        }
        
        return { tankId: t.id, name: t.tankName, currentStock, quantity, rate: rt, amount, invoice };
      }).filter(p => p.quantity > 0);

      if (tankPurchases.length === 0) throw new Error("Enter item quantities to update stock.");

      const ledgerId = `${userId}_${orderdate}_${fuelType}_${Timestamp.now().toMillis()}`;
      await setDoc(doc(db, "purchase_ledger", ledgerId), {
        userId, 
        date: orderdate, 
        fuelType, 
        tankPurchases, 
        totalSales: totalLiters, // Keeping variable names clean/identical for structures
        totalRevenue: totalAmount, 
        updatedAt: Timestamp.now()
      }, { merge: true });

      // PLUS STOCK: Increments the matching tank document's current stock values
      for (const purchase of tankPurchases) {
        const tankRef = doc(db, "tanks", purchase.tankId);
        const tankSnap = await getDoc(tankRef);
        if (tankSnap.exists()) {
          const existingStock = parseFloat(tankSnap.data().currentStock || 0);
          await updateDoc(tankRef, { 
            currentStock: existingStock + purchase.quantity,
            purchaseRate: purchase.rate
          });
        }
      }

      Toast.fire({ icon: 'success', title: `${fuelType} Stock Inward Successfully Saved!` });
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally { setLoading(false); }
  };

  const filteredHistory = history.filter(h => h.date.includes(searchTerm));
  const currentItems = filteredHistory.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

  return (
    <div className="content-wrapper">
      <section className="content pt-2">
        <div className="container-fluid">
          
          {/* FUEL SELECTION TABS */}
          <div className="btn-group btn-group-toggle w-100 mb-2 shadow-sm">
            <label className={`btn btn-sm ${fuelType === 'MS' ? 'btn-success' : 'btn-light'}`}>
              <input type="radio" onClick={() => {setFuelType('MS'); setCurrentPage(1);}} /> MS (Petrol Stock)
            </label>
            <label className={`btn btn-sm ${fuelType === 'HSD' ? 'btn-primary' : 'btn-light'}`}>
              <input type="radio" onClick={() => {setFuelType('HSD'); setCurrentPage(1);}} /> HSD (Diesel Stock)
            </label>
          </div>

          {/* COMPACT ENTRY FORM */}
          <div className={`card card-outline shadow-sm mb-2 ${fuelType === 'MS' ? 'card-success' : 'card-primary'}`}>
            <div className="card-header py-1">
              <h3 className="card-title text-xs text-bold">{fuelType} Purchase Register - {moment(orderdate).format('DD/MM')}</h3>
              <input type="date" className="form-control form-control-sm float-right w-25" 
                value={orderdate} onChange={e => setOrderdate(e.target.value)} />
            </div>
            <div className="card-body p-0">
              <table className="table table-sm table-bordered m-0 text-center text-xs">
                <thead className="bg-light">
                  <tr>
                    <th>Storage Tank</th>
                    <th>Purchase Rate (₹)</th>
                    <th>Current Stock (L)</th>
                    <th>Purchased Qty (Liters)</th>
                    <th>Invoice / Ref No.</th>
                    <th className="bg-dark text-white">Net Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {tanks.map(t => {
                    const currentStock = parseFloat(purchaseData[`${t.id}_current`] || 0);
                    const quantity = parseFloat(purchaseData[`${t.id}_quantity`] || 0);
                    const rt = parseFloat(purchaseData[`${t.id}_rate`] || 0);
                    
                    const amountNumeric = quantity * rt;
                    
                    return (
                      <tr key={t.id}>
                        <td className="align-middle"><b>{t.tankName}</b></td>
                        <td className="p-0">
                          <input type="number" step="0.01" className="form-control form-control-xs text-center border-0 font-weight-bold text-primary" value={rt} onChange={(e) => handleInputChange(`${t.id}_rate`, e.target.value)} />
                        </td>
                        <td className="align-middle bg-light">{currentStock} L</td>
                        <td className="p-0">
                          <input type="number" step="0.01" className="form-control form-control-xs text-center font-weight-bold" placeholder="+ Enter Qty" value={purchaseData[`${t.id}_quantity`]} onChange={(e) => handleInputChange(`${t.id}_quantity`, e.target.value)} />
                        </td>
                        <td className="p-0">
                          <input type="text" className="form-control form-control-xs text-center" placeholder="Optional" value={purchaseData[`${t.id}_invoice`]} onChange={(e) => handleInputChange(`${t.id}_invoice`, e.target.value)} />
                        </td>
                        <td className="align-middle bg-dark text-white">₹ {amountNumeric.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button onClick={savePurchaseStock} disabled={loading} className={`btn btn-xs btn-block py-2 ${fuelType === 'MS' ? 'btn-success text-bold' : 'btn-primary text-bold'}`}>
                {loading ? 'Processing...' : `ADD TO ${fuelType} INVENTORY STOCK`}
              </button>
            </div>
          </div>

          {/* HISTORY TABLE */}
          <div className="card shadow-sm">
            <div className="card-header py-1 bg-navy">
              <h3 className="card-title text-xs mt-1">{fuelType} Inward Stock Log</h3>
              <div className="card-tools">
                <input type="text" className="form-control form-control-sm h-75" placeholder="Search Date..." 
                  onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </div>
            <div className="card-body p-0">
              <table className="table table-xs table-bordered text-center m-0">
                <thead className="bg-secondary text-xs">
                  <tr>
                    <th>Date</th>
                    {tanks.map(t => <th key={t.id}>{t.tankName}</th>)}
                    <th className="bg-success">Total Added</th>
                    <th className="bg-success text-white">Total Value</th>
                    <th><i className="fas fa-cog"></i></th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {currentItems.map(row => (
                    <tr key={row.id}>
                      <td className="align-middle"><b>{moment(row.date).format('DD-MMM-YYYY')}</b></td>
                      {tanks.map(t => <td key={t.id} className="align-middle text-success font-weight-bold">+{row.tankPurchases?.find(x => x.tankId === t.id)?.quantity?.toFixed(2) || '0.00'} L</td>)}
                      <td className="align-middle font-weight-bold">{row.totalSales?.toFixed(2)} L</td>
                      <td className="align-middle"><b>₹{row.totalRevenue?.toFixed(2)}</b></td>
                      <td className="p-0 align-middle">
                        <button onClick={() => deleteEntry(row.id)} className="btn btn-link text-danger btn-xs"><i className="fas fa-trash"></i></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Purchasestock;